import { ChangeEvent, KeyboardEvent, RefObject, SyntheticEvent, useEffect, useRef, useState } from "react"
import Configs from "../../Configs"
import ProviderRoleService, { IProviderPermission, IProviderRole } from "../../services/ProviderRoleService"
import ProviderPermissionService from "../../services/ProviderPermissionService"
import { Alert, Button, Card, Col, Container, Form, InputGroup, Modal, Row, Table } from "react-bootstrap"
import ContentHeader from "../../components/ContentHeader"
import ContentBody from "../../components/ContentBody"
import Pagination from "../../components/Pagination"
import DeleteConfirmation from "../../components/DeleteConfirmation"
import useAppContext from "../../hooks/useAppContext"
import useAuthContext from "../../hooks/useAuthContext"
import Permissions from "../../constants/Permissions"

function ProviderRoles() {
    const { hasPermission } = useAuthContext()
    const { state } = useAppContext()

    const [tableData, setTableData] = useState({
        data: new Array<IProviderRole>(),
        hasNext: false,
        hasPrevious: false,
        pageIndex: 1,
        pageSize: Configs.DEFAULT_PAGE_SIZE,
        totalPages: 0,
        totalRecords: 0
    })

    const initFormData = {
        id: 0,
        name: "",
        permissions: new Array<IProviderPermission>()
    } as IProviderRole

    const [message, setMessage] = useState("")
    const [messageFormModal, setMessageFormModal] = useState("")
    const [validatedFormModal, setValidatedFormModal] = useState(false)
    const [formData, setFormData] = useState(initFormData)
    const [permissions, setPermissions] = useState(new Array<IProviderPermission>())
    const [currentPageIndex, setCurrentPageIndex] = useState(1)
    const [deletedItem, setDeletedItem] = useState({
        id: 0,
        name: ""
    })

    const keywordRef = useRef<HTMLInputElement>(null)
    const formRef = useRef<HTMLFormElement>(null)

    const FilterFn = async (pageIndex: number) => {
        if (state.currentProviderId) {
            let pageSize = Configs.DEFAULT_PAGE_SIZE
            let keyword = keywordRef.current?.value || ""

            const res = await ProviderRoleService.filter(state.currentProviderId, pageIndex, pageSize, keyword)

            if (res?.isSuccess) {
                setTableData(res.data)
            } else {
                console.log(res?.message)
            }

            setCurrentPageIndex(pageIndex)
        }
    }

    const GetPermissions = async () => {
        if (state.currentProviderId) {
            const res = await ProviderPermissionService.listAll(state.currentProviderId)

            if (res?.isSuccess) {
                setPermissions(res.data.map((item: IProviderPermission) => {
                    return { ...item, checked: false }
                }))
            } else {
                console.log(res?.message)
            }
        }
    }

    useEffect(() => {
        keywordRef.current?.focus()

        FilterFn(1)

        GetPermissions()
    }, [state.currentProviderId])

    const handleSearch = () => {
        FilterFn(1)
    }

    const handleSearchInputKeyPress = (e: KeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Enter') {
            FilterFn(1)
        }
    }

    const handlePageChange = (pageNumber: number) => {
        FilterFn(pageNumber)
    }

    const handleFormControlChange = (e: ChangeEvent<HTMLInputElement>) => {
        setFormData({ ...formData, [e.target.name]: e.target.value })
    }

    const handleAddNew = () => {
        setFormData(initFormData)

        handleShowModal()
    }

    const ClearCheckedPermissions = () => {
        setPermissions(permissions.map(p => {
            return { ...p, checked: false }
        }))
    }

    const handleEdit = async (id: number) => {
        if (state.currentProviderId) {
            const res = await ProviderRoleService.get(state.currentProviderId, id)

            if (res?.isSuccess) {
                setFormData({
                    ...initFormData,
                    id: res.data.id,
                    name: res.data.name,
                    permissions: res.data.permissions
                })

                setPermissions(permissions.map(permission => {
                    let checked = (res.data.permissions as IProviderPermission[]).some(p => { return p.id === permission.id })
                    return { ...permission, checked: checked }
                }))

                handleShowModal()
            } else {
                console.log(res?.message)
            }
        }
    }

    const handlePermissionsChange = (permissionId: number) => {
        setPermissions(permissions.map(permission => {
            if (permission.id === permissionId) {
                return { ...permission, checked: !permission.checked }
            } else {
                return permission
            }
        }))
    }

    const handleSave = async (e: SyntheticEvent) => {
        e.preventDefault()
        if (state.currentProviderId) {
            if (formRef.current?.checkValidity()) {
                const data = {
                    ...formData,
                    permissions: permissions.filter(permission => {
                        return permission.checked === true
                    })
                }

                if (formData.id > 0) {
                    const res = await ProviderRoleService.update(state.currentProviderId, formData.id, data)

                    if (res?.isSuccess) {
                        FilterFn(currentPageIndex)
                        handleCloseModal()
                        setMessage(res.message)
                    } else {
                        setMessageFormModal(res?.message)
                    }
                } else {
                    const res = await ProviderRoleService.create(state.currentProviderId, data)

                    if (res?.isSuccess) {
                        FilterFn(1)
                        handleCloseModal()
                        setMessage(res.message)
                    } else {
                        setMessageFormModal(res?.message)
                    }
                }
            } else {
                setValidatedFormModal(true)
            }
        }
    }

    const [showModal, setShowModal] = useState(false)
    const handleCloseModal = () => {
        setShowModal(false)
        ClearCheckedPermissions()
        setValidatedFormModal(false)
        setMessageFormModal("")
    }
    const handleShowModal = () => {
        setShowModal(true)
        setMessage("")
    }

    const modalTitle = formData.id > 0 ? "Edit Role" : "Add New Role"

    const [displayConfirmationModal, setDisplayConfirmationModal] = useState(false)

    const hideConfirmationModal = () => {
        setDeletedItem({
            id: 0,
            name: ""
        })

        setDisplayConfirmationModal(false)
    }

    const showDeleteConfirmation = (id: number, name: string) => {
        setDeletedItem({
            id: id,
            name: name
        })

        setDisplayConfirmationModal(true)
        setMessage("")
    }

    const handleDelete = async () => {
        if (state.currentProviderId) {
            const res = await ProviderRoleService.delete(state.currentProviderId, deletedItem.id)

            if (res?.isSuccess) {
                FilterFn(currentPageIndex)
            }

            hideConfirmationModal()
            setMessage(res.message)
        }
    }

    const PermissionCheckBoxes = () => {
        var list = new Array<any>()

        var arr = permissions.map((p) => {
            return p.name.split(".")[1]
        })

        var modules = arr.filter(function (item, pos) {
            return arr.indexOf(item) === pos
        })

        modules.forEach(mod => {
            list.push(<div key={mod} style={{ marginTop: ".5rem" }}>{mod}</div>)

            let actions = permissions.filter(item => {
                return item.name.startsWith("Permissions." + mod + ".")
            })

            actions.forEach(permission => {
                list.push(<Form.Check
                    key={permission.id}
                    type="checkbox"
                    id={`permission-${permission.id}`}
                    label={permission.name.split(".")[2]}
                    checked={permission.checked}
                    onChange={() => handlePermissionsChange(permission.id)}
                    style={{ display: "inline-block", marginLeft: "1rem" }}
                />)
            })
        })

        return list
    }

    return (
        <>
            <ContentHeader title="Roles" />

            <ContentBody>
                <Container fluid>
                    <Row>
                        <Col xs={12}>
                            <Card>
                                <Card.Header>
                                    {hasPermission(Permissions.ProviderRoles.Create) &&
                                        <Button variant="info" size="sm" onClick={handleAddNew}>
                                            <i className="fas fa-plus"></i> Add new role
                                        </Button>
                                    }

                                    <div className="card-tools">
                                        <InputGroup size="sm">
                                            <Form.Control
                                                type="search"
                                                placeholder="Search"
                                                ref={keywordRef as RefObject<HTMLInputElement>}
                                                onKeyUp={handleSearchInputKeyPress}
                                            />
                                            <Button variant="info" size="sm" onClick={handleSearch}>
                                                <i className="fas fa-search"></i>
                                            </Button>
                                        </InputGroup>
                                    </div>
                                </Card.Header>
                                <Card.Body>
                                    {message !== "" && <Alert variant="info" onClose={() => setMessage("")} dismissible>{message}</Alert>}
                                    <Table responsive striped bordered hover>
                                        <thead>
                                            <tr>
                                                <th>Name</th>
                                                <th style={{ textAlign: "center", width: "80px" }}>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {
                                                tableData.data.map(item =>
                                                    <tr key={item.id}>
                                                        <td>{item.name}</td>
                                                        <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                                                            {hasPermission(Permissions.ProviderRoles.Edit) &&
                                                                <Button variant="warning" size="sm" className="mr-1" onClick={() => handleEdit(item.id)}><i className="fas fa-pencil-alt"></i> Edit</Button>
                                                            }
                                                            {hasPermission(Permissions.ProviderRoles.Delete) &&
                                                                <Button variant="danger" size="sm" onClick={() => showDeleteConfirmation(item.id, item.name)}><i className="fas fa-trash-alt"></i> Delete</Button>
                                                            }
                                                        </td>
                                                    </tr>
                                                )
                                            }
                                        </tbody>
                                    </Table>
                                </Card.Body>
                                <Card.Footer>
                                    <Pagination hasNext={tableData.hasNext} hasPrevious={tableData.hasPrevious} pageIndex={tableData.pageIndex} pageSize={tableData.pageSize} totalPages={tableData.totalPages} totalRecords={tableData.totalRecords} handlePageChange={handlePageChange} />
                                </Card.Footer>
                            </Card>
                        </Col>
                    </Row>
                </Container>
            </ContentBody>

            <Modal show={showModal} onHide={handleCloseModal} backdrop="static" keyboard={false} size="lg">
                <Modal.Header closeButton>
                    <Modal.Title>{modalTitle}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <Form noValidate validated={validatedFormModal} ref={formRef as RefObject<HTMLFormElement>}>
                        <Form.Group className="mb-3" controlId="name">
                            <Form.Label>Name</Form.Label>
                            <Form.Control type="text" placeholder="Name" name="name" required value={formData.name} onChange={(e) => handleFormControlChange(e as any)} />
                            <Form.Control.Feedback type="invalid">Please enter Name.</Form.Control.Feedback>
                        </Form.Group>
                        <Form.Group className="mb-3">
                            <Form.Label>Permissions</Form.Label>
                            <Card>
                                <Card.Body style={{ maxHeight: "500px", overflow: "auto", paddingTop: "0" }}>{PermissionCheckBoxes()}</Card.Body>
                            </Card>
                        </Form.Group>
                    </Form>
                    {messageFormModal !== "" && <Alert variant="danger" onClose={() => setMessageFormModal("")} dismissible>{messageFormModal}</Alert>}
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="light" onClick={handleCloseModal}><i className="fas fa-times"></i> Close</Button>
                    <Button variant="info" onClick={handleSave}><i className="fas fa-save"></i> Save</Button>
                </Modal.Footer>
            </Modal >

            <DeleteConfirmation show={displayConfirmationModal} onConfirm={handleDelete} onHide={hideConfirmationModal} id={deletedItem.id} name={deletedItem.name} />
        </>
    )
}

export default ProviderRoles